import React, { useContext, useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { show_toast } from "utils/helpers";
import { ModalContext } from "context/ModalContext";
import { appendDmvicMember, getDmvicConfig } from "services/dvmvic";

export default function AddDmvicUnderwriter({
  fetchDmvics,
  underwriters = [],
  onSuccess,
}) {
  const { modalClose } = useContext(ModalContext);

  const [dmvicConfig, setDmvicConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(null);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        setConfigLoading(true);
        const res = await getDmvicConfig();
        setDmvicConfig(res.data);
      } catch (err) {
        console.error("Failed to load DMVIC config:", err);
        setConfigError("Could not load DMVIC settings. Please try again.");
        show_toast("Failed to load configuration", "error");
      } finally {
        setConfigLoading(false);
      }
    };

    loadConfig();
  }, []);

  const initialValues = {
    underwriter: "",
    memberId: "",           // ← free text now
  };

  const validationSchema = Yup.object({
    underwriter: Yup.string().required("Please select an underwriter"),
    memberId: Yup.string()
      .required("Member ID is required")
      .trim()
      // Add more rules depending on your format requirements, examples:
      // .matches(/^[A-Z0-9]{5,12}$/, "Member ID must be 5–12 alphanumeric characters")
      // .min(4, "Member ID is too short")
      // .max(20, "Member ID is too long")
      ,
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (!dmvicConfig) {
      show_toast("DMVIC configuration not loaded", "error");
      setSubmitting(false);
      return;
    }

    try {
      const payload = {
        underwriterRef: values.underwriter,
        memberId: values.memberId.trim(),     // ← comes from text input
        memberType: "UNDERWRITER",
        // status: "ACTIVE",
        // effectiveDate: new Date().toISOString(),
        // ... other fields your API expects
      };

      await appendDmvicMember(payload);

      show_toast("Underwriter added to DMVIC successfully!", "success");

      fetchDmvics?.();
      resetForm();
      modalClose?.();
      onSuccess?.();
    } catch (err) {
      console.error("Failed to add DMVIC member:", err);
      const message = err?.response?.data?.message || "Failed to add underwriter";
      show_toast(message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (configLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">...</span>
        </div>
      </div>
    );
  }

  if (configError || !dmvicConfig) {
    return (
      <div className="text-center py-5 text-danger">
        <p>{configError || "DMVIC configuration unavailable"}</p>
        <small>Please try again or contact support.</small>
      </div>
    );
  }

  return (
    <div className="container p-4 bg-light rounded shadow-sm">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values }) => (
          <Form>
            <div className="mb-4">
              <label htmlFor="underwriter" className="form-label fw-bold">
                Select Underwriter
              </label>
              <Field
                as="select"
                name="underwriter"
                className="form-select"
                disabled={underwriters.length === 0 || isSubmitting}
              >
                <option value="">-- Choose underwriter --</option>
                {underwriters.map((u) => (
                  <option
                    key={u.intermediaryUnderwriterRef || u.id || u.ref}
                    value={u.intermediaryUnderwriterRef || u.id || u.ref}
                  >
                    {u.name || u.underwriterName || u.description || "Unnamed Underwriter"}
                  </option>
                ))}
              </Field>
              <ErrorMessage
                name="underwriter"
                component="div"
                className="text-danger mt-1 small"
              />
            </div>

            {/* Changed from select → text input */}
            <div className="mb-4">
              <label htmlFor="memberId" className="form-label fw-bold">
                Member ID
              </label>
              <Field
                type="text"
                name="memberId"
                className="form-control"
                placeholder="Enter Member ID (e.g. UW12345, MBR-8901, etc.)"
                disabled={isSubmitting}
                autoComplete="off"
              />
              <ErrorMessage
                name="memberId"
                component="div"
                className="text-danger mt-1 small"
              />
              {/* Optional helper text */}
              <small className="form-text text-muted mt-1">
                Enter the exact Member ID provided by DMVIC
              </small>
            </div>

            <div className="text-center mt-5">
              <button
                type="submit"
                className="btn btn-primary px-5 py-2"
                disabled={isSubmitting || underwriters.length === 0}
              >
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Adding...
                  </>
                ) : (
                  "Add Underwriter to DMVIC"
                )}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}